import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, TemplateRef, ViewChild, ViewChildren, DestroyRef, inject } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbOffcanvas, NgbOffcanvasRef } from '@ng-bootstrap/ng-bootstrap';
import { OrderByPipe } from 'ngx-pipes';
import { Subscription, firstValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { IArea } from 'src/app/_models/area.vm';
import { IPeriod } from 'src/app/_models/period.vm';
import { ISystemLevel } from 'src/app/_models/systemLevel.vm';

import { ApiService } from 'src/app/_services/api.service';
import { GoogleAnalyticsService } from 'src/app/_services/google-analytics.service';
import { MobileDockService } from 'src/app/_services/mobile-dock.service';
import { SessionService } from 'src/app/_services/session.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ErrorReportingService } from '../../_services/error-reporting.service';

@Component({
    selector: 'app-select-period-area',
    templateUrl: './select-period-area.component.html',
    styleUrls: ['./select-period-area.component.scss'],
    providers: [SessionService, OrderByPipe],
    standalone: false
})
export class SelectPeriodAreaComponent implements OnInit, OnDestroy {
  private readonly errorReporting = inject(ErrorReportingService);

  // Ties every subscription below to this component's lifetime, so a response
  // that arrives after the user has navigated away is dropped instead of
  // writing into a view that no longer exists.
  private readonly destroyRef = inject(DestroyRef);


  /** Held here so the docked bar and the sheet can show the same wording. */
  public readonly modelledNotice: string =
    'Figures before June 2026 are estimated mapped to the new organisational structure, not data as originally reported.';

  @Input() public outcomes?: string;
  @Output() public currentPeriod = new EventEmitter<IPeriod>();
  @Output() public currentArea = new EventEmitter<IArea>();
  /** Emitted whenever the Reported/Modelled toggle changes, and once on init. */
  @Output() public currentDataType = new EventEmitter<string>();
  /**
   * The SystemLevelTypeID behind the toggle's current selection, passed to the
   * pages that consume this component so their own indicator calls stay scoped to
   * the same Standard/Modelled selection as the area dropdown. Emitted once the
   * area/systemLevelTypes lookup resolves, and again on every toggle change.
   */
  @Output() public currentSystemLevelTypeId = new EventEmitter<number>();
  /** Emitted whenever checkingIfAreaExists changes, so consuming pages can show their own loading state while it is in progress. */
  @Output() public checkingArea = new EventEmitter<boolean>();

  @ViewChildren('dataTypeOption') private dataTypeOptionEls: QueryList<ElementRef<HTMLButtonElement>>;
  @ViewChild('selectorSheet') private selectorSheet: TemplateRef<any>;
  @ViewChild('dock', { static: true }) private dockRef: ElementRef<HTMLElement>;

  public dataType: string = 'reported';
  /** Raw response from area/systemLevelTypes, backing dataTypeToggleOptions below. */
  public systemLevelTypes: Array<any>;

  /** True on phones and tablets, where the bar is docked and its controls open in a sheet. */
  public isCompact: boolean = false;
  public sheetOpen: boolean = false;

  public queryParams
  public periodId: number;
  public areaId: number;
  public systemLevelId: number;

  public isSelectedAreaInTheSelectedTimePeriod: boolean = true;
  public periods: Array<IPeriod>;
  public systemLevels: Array<ISystemLevel>;
  public searchResults: Array<IArea>;
  public searchError: boolean = false;
  public partialAreaName = new UntypedFormControl();
  private _checkingIfAreaExists: boolean = false;
  public get checkingIfAreaExists(): boolean {
    return this._checkingIfAreaExists;
  }
  public set checkingIfAreaExists(value: boolean) {
    this._checkingIfAreaExists = value;
    this.checkingArea.emit(value);
  }

  /** True while the bar is working out which period and area apply to this page. */
  public selectorLoading: boolean = true;
  public selectorError: boolean = false;
  public periodsLoading: boolean = false;
  public levelsLoading: boolean = false;
  public areasLoading: boolean = false;

  public selectedPeriod: IPeriod;
  public selectedPeriodBackUp: IPeriod;
  /** Which change the no-data banner is currently responding to, so its wording and revert action match what the user actually did. */
  public lastMismatchCause: 'period' | 'dataType' = 'period';
  public selectedSystemLevel: ISystemLevel;
  /** The level whose areas are shown in the panel. Not necessarily the selected one. */
  public activeSystemLevel: ISystemLevel;
  public selectedArea: IArea;
  public explorerData: number = 1;
  public outcomesData: number = 2;
  public outcomesPage: boolean;

  /** Set when a period change should re-apply the current area once it is confirmed to exist. */
  private pendingApply: boolean = false;

  /**
   * Bumped every time loadSystemLevels starts a fresh check. A response is only
   * applied if this still matches the value captured when that particular check
   * began, so a period or data type change made while an earlier one is still in
   * flight cannot have its stale result land on top of the newer selection.
   */
  private selectionGeneration: number = 0;

  private readonly subscriptions = new Subscription();
  private dockObserver: ResizeObserver;
  private sheetRef: NgbOffcanvasRef;


  constructor(
    private apiService: ApiService,
    private sessionService: SessionService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private router: Router,
    private route: ActivatedRoute,
    private order: OrderByPipe,
    private offcanvas: NgbOffcanvas,
    private dock: MobileDockService
  ) {
    // Subscribe to search input changes
    // https://blog.thoughtram.io/angular/2016/01/06/taking-advantage-of-observables-in-angular2.html
    this.partialAreaName.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe(partialAreaName => {
        this.areasLoading = true;
        this.getAreasBySearch(partialAreaName);
      }
      );
  }

  ngOnInit() {
    this.subscriptions.add(this.dock.compact$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(compact => this.setCompact(compact)));
    // The docked bar grows and shrinks as notices come and go, so its height is
    // measured rather than assumed.
    this.dockObserver = new ResizeObserver(() => this.publishDockHeight());
    this.dockObserver.observe(this.dockRef.nativeElement);

    this.outcomesPage = this.router.url.split('?')[0] == '/outcomes';
    this.currentDataType.emit(this.dataType);

    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.periodId = params['period'];
      this.systemLevelId = params['level'];
      this.areaId = params['area'];
      this.queryParams = params;
    });

    this.initialiseSelector();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.dockObserver?.disconnect();
    this.closeSheet();
    // Released before the height is cleared, so the quick links can take the slot
    // over on the page being navigated to.
    this.dock.setSelectorDocked(false);
    this.dock.setHeight(null);
  }

  // Docked bar and sheet, below the compact breakpoint ------------------------

  private setCompact(compact: boolean): void {
    if (this.isCompact == compact) {
      return;
    }
    this.isCompact = compact;
    // The selector takes the slot at the foot of the viewport, which the quick links
    // to the tools give way to.
    this.dock.setSelectorDocked(compact);
    if (!compact) {
      this.closeSheet();
      // Back in the flow, so the page has nothing to keep clear of.
      this.dock.setHeight(null);
    }
    // Straight away, so the page is clear of the bar from the first frame, then
    // again once the bar has settled into its docked shape.
    this.publishDockHeight();
    requestAnimationFrame(() => this.publishDockHeight());
  }

  /**
   * The page, the cookie banner and the service notice all sit clear of the docked
   * bar by this measurement rather than by a fixed guess, because the bar carries
   * whichever notices apply to the current selection.
   */
  private publishDockHeight(): void {
    if (!this.isCompact || !this.dockRef) {
      return;
    }
    this.dock.setHeight(this.dockRef.nativeElement.getBoundingClientRect().height);
  }

  public openSheet(): void {
    if (this.sheetOpen || !this.selectorSheet) {
      return;
    }
    this.sheetOpen = true;
    this.sheetRef = this.offcanvas.open(this.selectorSheet, {
      position: 'bottom',
      panelClass: 'cvdp-sheet',
      backdropClass: 'cvdp-sheet-backdrop',
      ariaLabelledBy: 'cvdpSheetTitle',
      scroll: false
    });
    // Closed and dismissed both settle the same way: Escape, the backdrop and Done
    // are all just ways of finishing with the sheet.
    const finished = () => {
      this.sheetOpen = false;
      this.sheetRef = null;
    };
    this.sheetRef.result.then(finished, finished);
  }

  public closeSheet(): void {
    this.sheetRef?.dismiss();
  }

  /**
   * The Reported/Modelled toggle, labelled and identified from area/systemLevelTypes
   * once it has loaded. Falls back to the previous static wording until then, since
   * the "Modelled" entry is matched by name and everything else is treated as the
   * reported option.
   */
  public get dataTypeToggleOptions(): Array<{ key: string, id: number, name: string }> {
    const modelled = this.systemLevelTypes?.find(t => t.SystemLevelTypeName == 'Modelled');
    const reported = this.systemLevelTypes?.find(t => t.SystemLevelTypeName != 'Modelled');
    return [
      { key: 'reported', id: reported?.SystemLevelTypeID, name: reported?.SystemLevelTypeName || 'Reported' },
      { key: 'modelled', id: modelled?.SystemLevelTypeID, name: modelled?.SystemLevelTypeName || 'Modelled' }
    ];
  }

  private dataTypeLabel(key: string): string {
    return this.dataTypeToggleOptions.find(o => o.key == key)?.name;
  }

  /** The SystemLevelTypeID for the currently selected Reported/Modelled option, passed to every area and indicator call. */
  public get systemLevelTypeId(): number {
    return this.dataTypeToggleOptions.find(o => o.key == this.dataType)?.id;
  }

  /** Keeps the two buttons themselves stable across change detection, since dataTypeToggleOptions returns fresh objects each time. */
  public trackByOptionKey(_index: number, opt: { key: string }): string {
    return opt.key;
  }

  /**
   * The toggle only makes sense once an area is selected, and only where that area
   * has a Modelled (or Standard) counterpart to switch to - a null EquivalentAreaID
   * means this area has no equivalent under the other data type, so there is
   * nothing to toggle to.
   */
  public get showDataTypeToggle(): boolean {
    return !!this.selectedArea && this.selectedArea.EquivalentAreaID != null;
  }

  /**
   * selectedArea itself is kept around even when it no longer exists under the
   * current period/data type, so "keep previous" has something to restore. This
   * is the version shown to the user: null once the banner is up, so the trigger
   * and dock read "Select an area" instead of naming an area that is no longer valid.
   */
  public get displayableSelectedArea(): IArea {
    return this.isSelectedAreaInTheSelectedTimePeriod ? this.selectedArea : null;
  }

  /** The second line of the docked bar: the rest of what the figures on the page describe. */
  public get dockSummary(): string {
    if (this.checkingIfAreaExists) {
      return 'Checking this area...';
    }
    const parts: Array<string> = [];
    if (this.selectedPeriod) {
      parts.push(this.selectedPeriod.TimePeriodName);
    }
    parts.push(this.dataTypeLabel(this.dataType));
    return parts.join(' · ');
  }

  /** Spelled out for screen readers, where the separators above would read as punctuation. */
  public get dockAriaLabel(): string {
    const area = this.displayableSelectedArea ? this.displayableSelectedArea.AreaName : 'none selected';
    const period = this.selectedPeriod ? this.selectedPeriod.TimePeriodName : 'none selected';
    const type = this.dataTypeLabel(this.dataType).toLowerCase();
    return `Change selection. Area: ${area}. Time period: ${period}. Showing ${type} data.`;
  }

  /**
   * Outcomes reports on its own set of areas: the same region carries a different
   * AreaID there (East of England is 8151 in the reported data and 7666 in
   * outcomes), and the periods are different too. A selection made on one side is
   * therefore meaningless on the other, so each keeps its own stored selection.
   */
  private get storageKey(): string {
    return this.outcomesPage ? 'cvdp-dit-outcomes' : 'cvdp-dit';
  }

  public get periodHelperText(): string {
    // Keyed off the route as well as the title input, which is only set once the
    // outcomes intro has been visited. Without this the outcomes periods could be
    // listed under the standard wording.
    return (this.outcomesPage || this.outcomes)
      ? 'A time period displays data on outcomes in the below mentioned twelve months, for CVDPREVENT patients identified at the beginning of each period.'
      : 'A time period displays all available data up to the end of that month.';
  }

  // Establishing the selection for this page ---------------------------------

  /** Also the retry handler for the error state. */
  public initialiseSelector(): void {
    this.selectorError = false;

    const stored = this.readStoredSelection();
    // Set synchronously, before anything else in this method fires a request, so
    // the toggle reflects the stored preference straight away rather than sitting
    // on "Reported" until the area check has come back one way or the other.
    if (stored && stored.dataType) {
      this.dataType = stored.dataType;
      this.currentDataType.emit(this.dataType);
    }

    if (stored && this.canRestoreWithoutChecking(stored)) {
      this.applyStoredSelection(stored);
      return;
    }

    this.selectorLoading = true;
    const request = this.outcomesPage
      ? this.apiService.getOutcomePeriods(this.outcomesData)
      : this.apiService.getPeriods(this.explorerData);

    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      success => {
        this.periods = success.timePeriodList || [];
        if (!this.periods.length) {
          this.failSelector();
          return;
        }

        // A period from the URL wins, then the one stored for this page, then the
        // most recent. Anything that is not in this page's list is discarded.
        this.selectedPeriod =
          this.periods.find(p => p.TimePeriodID == this.periodId) ||
          this.periods.find(p => stored && p.TimePeriodID == stored.period.TimePeriodID) ||
          this.periods[0];
        this.selectedPeriodBackUp = this.selectedPeriod;

        // A stored Modelled selection is not from a link and is not necessarily
        // wrong, just unverified on this page - so it is checked the same way a
        // live toggle would check it, rather than treated as a stale link and
        // dropped by restoreArea/clearSelection if this page's levels come back
        // empty for it.
        const cameFromLink = !!(this.areaId || this.systemLevelId);
        if (stored && stored.dataType == 'modelled' && !cameFromLink) {
          this.lastMismatchCause = 'dataType';
          this.selectedArea = stored.area;
          this.systemLevelId = stored.level;
          this.checkingIfAreaExists = true;
          this.loadSystemLevels(this.selectedPeriod.TimePeriodID);
          return;
        }

        const restoreAreaId = this.areaId || (stored ? stored.area.AreaID : null);
        const restoreLevelId = this.systemLevelId || (stored ? stored.level : null);
        this.loadSystemLevels(this.selectedPeriod.TimePeriodID, restoreAreaId, restoreLevelId);
      },
      error => {
        this.errorReporting.report('Initialise selector', error);
        this.failSelector();
      }
    )
  }

  private failSelector(): void {
    this.selectorLoading = false;
    this.selectorError = true;
  }

  /**
   * Insights, Improvement and Explorer all report on the same periods and the same
   * areas, and the stored selection is already keyed to this data domain, so one
   * stored by any of them can be shown straight away rather than re-checked on
   * every page. That keeps switching between them instant.
   *
   * Two things still rule it out: a link carrying its own selection has to be
   * verified rather than trusted; and Modelled's coverage is not reliable enough
   * yet to trust without asking area/systemLevel again on this page - unlike
   * Standard, it can go from valid to empty between one page and the next.
   */
  private canRestoreWithoutChecking(stored: any): boolean {
    if (!stored.level || !stored.period.TimePeriodID || !stored.area.AreaID) {
      return false;
    }
    const urlAgreesWithStored =
      (!this.periodId || this.periodId == stored.period.TimePeriodID) &&
      (!this.areaId || this.areaId == stored.area.AreaID) &&
      (!this.systemLevelId || this.systemLevelId == stored.level);
    if (!urlAgreesWithStored) {
      return false;
    }
    if (stored.dataType == 'modelled') {
      return false;
    }
    return true;
  }

  private applyStoredSelection(stored: any): void {
    this.selectedPeriod = stored.period;
    this.selectedPeriodBackUp = stored.period;
    this.selectedArea = stored.area;
    this.systemLevelId = stored.level;
    this.dataType = stored.dataType || this.dataType;
    this.isSelectedAreaInTheSelectedTimePeriod = true;
    this.selectorLoading = false;
    this.loadSystemLevelTypes(stored.period.TimePeriodID);
    this.router.navigate([], {
      queryParams: { period: stored.period.TimePeriodID, level: stored.level, area: stored.area.AreaID },
      queryParamsHandling: 'merge'
    });
    this.currentPeriod.emit(this.selectedPeriod);
    this.currentArea.emit(this.selectedArea);
    this.currentDataType.emit(this.dataType);
  }

  /** The period list is only needed once the dropdown is opened. */
  public onPeriodDropdownOpened(): void {
    if (this.periods || this.periodsLoading) {
      return;
    }
    this.periodsLoading = true;
    const request = this.outcomesPage
      ? this.apiService.getOutcomePeriods(this.outcomesData)
      : this.apiService.getPeriods(this.explorerData);
    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      success => {
        this.periods = success.timePeriodList || [];
        this.periodsLoading = false;
        // If the period held in storage has since been withdrawn, move to the most
        // recent rather than leaving one on screen that no longer exists.
        const stillListed = this.periods.find(p => p.TimePeriodID == this.selectedPeriod?.TimePeriodID);
        if (!stillListed && this.periods.length) {
          this.onPeriodSelected(this.periods[0]);
        }
      },
      error => {
        this.errorReporting.report('On period dropdown opened', error);
        this.periodsLoading = false;
      }
    )
  }

  private loadSystemLevelTypes(periodId: number): void {
    this.apiService.getSystemLevelTypes(periodId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      success => {
        this.systemLevelTypes = success.systemLevelTypes || [];
        this.currentSystemLevelTypeId.emit(this.systemLevelTypeId);
      },
      error => this.errorReporting.report('Load system level types', error)
    );
  }

  private loadSystemLevels(periodId: number, restoreAreaId?: number, restoreLevelId?: number): void {
    // Bumped on every call, so a click made while a previous one is still in
    // flight can tell its own, now-superseded response apart from the one that
    // actually matches the current selection - without this, a period or data
    // type change made quickly after another could apply a stale response on
    // top of the newer state instead of being dropped.
    const generation = ++this.selectionGeneration;
    this.levelsLoading = true;
    const previousLevelId = this.activeSystemLevel?.SystemLevelID;
    this.activeSystemLevel = null;
    // systemLevelTypeId is not known until systemLevelTypes has loaded, and on a
    // freshly created component (a page navigation) that has not happened yet, so
    // getSystemLevels has to wait for it rather than firing in parallel - otherwise
    // it silently goes out with no type filter and the mismatch only surfaces once
    // something else (opening a level) makes its own, correctly filtered request.
    this.apiService.getSystemLevelTypes(periodId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      success => {
        if (generation !== this.selectionGeneration) {
          return;
        }
        this.systemLevelTypes = success.systemLevelTypes || [];
        this.currentSystemLevelTypeId.emit(this.systemLevelTypeId);
        this.fetchSystemLevels(periodId, previousLevelId, restoreAreaId, restoreLevelId, generation);
      },
      error => {
        this.errorReporting.report('Load system levels', error);
        if (generation !== this.selectionGeneration) {
          return;
        }
        // Carries on without a type filter rather than getting stuck, so a failed
        // lookup for the toggle's own data does not block the area dropdown too.
        this.fetchSystemLevels(periodId, previousLevelId, restoreAreaId, restoreLevelId, generation);
      }
    );
  }

  private fetchSystemLevels(periodId: number, previousLevelId: number, restoreAreaId: number, restoreLevelId: number, generation: number): void {
    this.apiService.getSystemLevels(periodId, this.systemLevelTypeId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      success => {
        if (generation !== this.selectionGeneration) {
          return;
        }
        this.levelsLoading = false;
        this.systemLevels = this.order.transform(success.systemLevels, 'SystemLevelOrder');

        const previousLevel = previousLevelId
          ? this.systemLevels.find(lvl => lvl.SystemLevelID == previousLevelId)
          : null;
        if (previousLevel) {
          this.onLevelActivated(previousLevel);
        }

        if (restoreAreaId) {
          this.restoreArea(restoreAreaId, restoreLevelId, generation);
        } else if (this.selectedArea) {
          // A period change with an area already chosen. This is the expensive
          // check, but it is a deliberate user action rather than page load.
          this.checkIfSelectedAreaExistsInTheSelectedTimePeriod(this.systemLevels, generation);
        } else {
          this.selectorLoading = false;
        }
      },
      error => {
        if (generation !== this.selectionGeneration) {
          return;
        }
        this.errorReporting.report('Fetch system levels', error);
        this.levelsLoading = false;
        this.failSelector();
      }
    )
  }

  /**
   * Confirms the area is genuinely reported at this level in this period before
   * showing it. Anything that cannot be confirmed is dropped, because a carried
   * over selection reads as though the figures on the page belong to it.
   */
  private restoreArea(areaId: number, levelId: number, generation: number): void {
    const level = levelId ? this.systemLevels.find(lvl => lvl.SystemLevelID == levelId) : null;
    if (!level || !this.selectedPeriod) {
      this.clearSelection();
      return;
    }
    this.apiService.getAreas(this.selectedPeriod.TimePeriodID, level.SystemLevelID, null, this.systemLevelTypeId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      success => {
        if (generation !== this.selectionGeneration) {
          return;
        }
        level.SystemLevelAreas = success.areaList;
        const match = (success.areaList || []).find(a => a.AreaID == areaId);
        if (!match) {
          this.clearSelection();
          return;
        }
        this.selectedSystemLevel = level;
        this.activeSystemLevel = level;
        this.systemLevelId = level.SystemLevelID;
        this.getArea(match, true);
      },
      error => {
        if (generation !== this.selectionGeneration) {
          return;
        }
        this.errorReporting.report('Restore area', error);
        this.clearSelection();
      }
    )
  }

  /** Drops an area that does not apply here, leaving the user to choose one. */
  private clearSelection(): void {
    this.selectedArea = null;
    this.selectedSystemLevel = null;
    this.systemLevelId = null;
    this.areaId = null;
    this.isSelectedAreaInTheSelectedTimePeriod = true;
    this.pendingApply = false;
    this.sessionService.removeSessionStorage(this.storageKey);
    this.selectorLoading = false;
    this.router.navigate([], {
      queryParams: { period: this.selectedPeriod ? this.selectedPeriod.TimePeriodID : null, level: null, area: null },
      queryParamsHandling: 'merge'
    });
  }

  private readStoredSelection(): any {
    const stored = this.sessionService.getSessionStorage(this.storageKey);
    if (!stored || !stored.period || !stored.area) {
      return null;
    }
    return stored;
  }

  /**
   * Keeps the toggle's preference in storage even when the area check that
   * follows a data type change finds no match and leaves the banner up, so the
   * choice still carries over to the next page rather than being silently lost.
   */
  private persistDataType(): void {
    const stored = this.sessionService.getSessionStorage(this.storageKey);
    if (stored) {
      this.sessionService.setSessionStorage(this.storageKey, { ...stored, dataType: this.dataType });
    }
  }

  public onAreaDropdownOpened(): void {
    if (!this.systemLevels && !this.levelsLoading && this.selectedPeriod) {
      this.loadSystemLevels(this.selectedPeriod.TimePeriodID);
    }
  }

  // Area lookups -------------------------------------------------------------

  /**
   * Answers one question: does the currently selected area still report under the
   * newly chosen period and data type?
   *
   * This used to walk every system level in turn, awaiting a full area list for
   * each, and carried on past the level that already answered it. Picking a Region
   * still pulled down the PCN and practice lists, which are the largest payloads
   * the API serves. /area/search filters on the same time period and system level
   * type and returns EquivalentAreaID, so one small request settles it instead.
   *
   * Level area lists are no longer pre-loaded as a side effect; the dropdown
   * already fetches and caches each level the first time it is opened.
   */
  async checkIfSelectedAreaExistsInTheSelectedTimePeriod(systemLevels?: Array<ISystemLevel>, generation?: number) {
    let matchingArea: any;
    // Nothing reports at all under this period and data type, so there is no area
    // to find and no reason to ask. Answering here also keeps the no-data case
    // resolving in the same tick, as the old level walk over an empty list did.
    const worthSearching = !systemLevels || systemLevels.length > 0;
    try {
      const response: any = worthSearching ? await firstValueFrom(this.apiService.getAreasBySearch(
        this.selectedArea.AreaName,
        this.selectedPeriod.TimePeriodID,
        this.systemLevelTypeId
      )) : null;

      if (generation != null && generation !== this.selectionGeneration) {
        // A later period or data type change has already started its own check;
        // its answer is the one that matters.
        return;
      }

      // Standard and Modelled are separate AreaID spaces for the same real-world
      // area, cross-linked by EquivalentAreaID, so a data type change has to match
      // on that rather than AreaID or every toggle would read as "area not found".
      // The name search can return near matches, which is why the match is still
      // made on identity rather than on the name itself.
      matchingArea = (response?.foundAreaList || []).find(a => {
        return a.AreaID == this.selectedArea.AreaID || a.AreaID == this.selectedArea.EquivalentAreaID;
      });
    } catch (error) {
      this.errorReporting.report('Confirming the selected area reports in this period', error);
    }
    if (generation != null && generation !== this.selectionGeneration) {
      return;
    }
    this.selectorLoading = false;
    this.isSelectedAreaInTheSelectedTimePeriod = !!matchingArea;

    // A period or data type change with an area already selected applies straight
    // away, as long as that area reports under the new combination. If it does
    // not, the warning banner offers a way back to the previous choice.
    const shouldApply = this.pendingApply && !!matchingArea;
    this.pendingApply = false;
    if (shouldApply) {
      // Left true until getArea's own fetch resolves and emits the confirmed area,
      // so a consuming page's loading state runs straight into its indicator
      // reload instead of a frame of stale content flashing in between.
      this.getArea(matchingArea);
    } else if (matchingArea) {
      // Reverting: the area's own figures have not changed, so there is nothing
      // to re-fetch, but the pages that consume this component still need telling
      // the selection is valid again after being cleared while the banner was up.
      this.checkingIfAreaExists = false;
      this.currentPeriod.emit(this.selectedPeriod);
      this.currentArea.emit(this.selectedArea);
    } else {
      // Nothing matches: tell consuming pages there is nothing valid to show
      // rather than leaving them displaying whatever they last had.
      this.checkingIfAreaExists = false;
      this.currentArea.emit(null);
    }
  }

  /** Loads a level's areas into its own cache, so hovering never refetches. */
  public onLevelActivated(level: ISystemLevel): void {
    if (!level || !this.selectedPeriod) {
      return;
    }
    this.activeSystemLevel = level;
    if (level.SystemLevelAreas || level.SystemLevelAreasLoading) {
      return;
    }
    level.SystemLevelAreasLoading = true;
    this.apiService.getAreas(this.selectedPeriod.TimePeriodID, level.SystemLevelID, null, this.systemLevelTypeId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      success => {
        level.SystemLevelAreas = success.areaList;
        level.SystemLevelAreasLoading = false;
      },
      error => {
        this.errorReporting.report('On level activated', error);
        level.SystemLevelAreasLoading = false;
      }
    )
  }

  public getAreasBySearch(partialAreaName): void {
    this.searchError = false;
    if (partialAreaName && partialAreaName.length > 2 && this.selectedPeriod) {
      this.apiService.getAreasBySearch(partialAreaName, this.selectedPeriod.TimePeriodID, this.systemLevelTypeId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        success => {
          this.searchResults = this.order.transform(success.foundAreaList, 'SystemLevelID');
        },
        error => {
          this.errorReporting.report('Get areas by search', error);
          // The completion handler does not run after an error, so clear the
          // spinner here or the panel sits loading for ever.
          this.searchResults = null;
          this.searchError = true;
          this.areasLoading = false;
        },
        () => {
          this.areasLoading = false;
        }
      )
    } else {
      this.searchResults = null;
      this.areasLoading = false;
    }
  }

  /**
   * @param restoring true when re-applying a stored selection on page load rather
   * than responding to a click, which must not be counted as a filter event.
   */
  public getArea(area: IArea, restoring: boolean = false): void {
    this.apiService.getArea(area.AreaID, this.selectedPeriod.TimePeriodID, this.systemLevelTypeId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      success => {
        this.selectedArea = success.areaDetails;
        const systemLevelId = this.systemLevelId ? this.systemLevelId : area.SystemLevelID;
        this.router.navigate([], { queryParams: { period: this.selectedPeriod.TimePeriodID, level: systemLevelId, area: area.AreaID }, queryParamsHandling: 'merge' });
        this.currentPeriod.emit(this.selectedPeriod);
        this.currentArea.emit(this.selectedArea);
        if (!restoring) {
          this.googleAnalyticsService.areafilterEvent("Area", "filter", "click", area.AreaCode, area.AreaID, area.AreaName, area.SystemLevelName);
        }
        this.sessionService.setSessionStorage(this.storageKey, { period: this.selectedPeriod, level: systemLevelId, area: this.selectedArea, dataType: this.dataType });
        this.selectorLoading = false;
        // Cleared last, after the confirmed area has already been emitted, so a
        // consuming page's own loading state (set while handling currentArea above)
        // is in place before this one lifts.
        this.checkingIfAreaExists = false;
      },
      error => {
        this.errorReporting.report('Get area', error);
        if (restoring) {
          this.clearSelection();
        } else {
          this.selectorLoading = false;
        }
        this.checkingIfAreaExists = false;
      }
    )
  }

  public setPeriod(period: IPeriod, calledFrom?: string): void {
    this.isSelectedAreaInTheSelectedTimePeriod = true;
    this.lastMismatchCause = 'period';
    if (this.selectedArea) {
      this.checkingIfAreaExists = true;
    }
    this.selectedPeriod = period;
    this.loadSystemLevels(period.TimePeriodID);
  }

  // Selector events ----------------------------------------------------------

  public onPeriodSelected(period: IPeriod): void {
    if (!period) {
      return;
    }
    if (this.selectedPeriod) {
      this.selectedPeriodBackUp = this.selectedPeriod;
    }
    this.pendingApply = !!this.selectedArea;
    this.setPeriod(period, 'timePeriodBtn');
  }

  public onAreaSelected(event: { area: IArea, fromSearch: boolean }): void {
    const area = event?.area;
    if (!area) {
      return;
    }
    if (!event.fromSearch && this.activeSystemLevel) {
      this.selectedSystemLevel = this.activeSystemLevel;
      this.systemLevelId = this.activeSystemLevel.SystemLevelID;
    } else if (area.SystemLevelID) {
      this.systemLevelId = area.SystemLevelID;
    }
    this.pendingApply = false;
    this.isSelectedAreaInTheSelectedTimePeriod = true;
    this.selectedPeriodBackUp = this.selectedPeriod;
    this.getArea(area);
    // Choosing an area is the last step, so the sheet has done its job. A period on
    // its own leaves it open, since an area usually follows.
    this.closeSheet();
  }

  /** Puts the previous time period back after a combination with no data. */
  public revertPeriod(): void {
    this.pendingApply = false;
    this.isSelectedAreaInTheSelectedTimePeriod = true;
    if (this.selectedPeriodBackUp) {
      this.setPeriod(this.selectedPeriodBackUp);
    }
  }

  /**
   * Puts the previous data type back after a combination with no data. There are
   * only ever the two options, so "previous" is just "the other one" - this also
   * covers a Modelled selection restored on page load, which never went through
   * setDataType and so has no explicit backup to fall back on.
   */
  public revertDataType(): void {
    this.pendingApply = false;
    this.isSelectedAreaInTheSelectedTimePeriod = true;
    this.setDataType(this.dataType == 'modelled' ? 'reported' : 'modelled');
  }

  /** The single handler behind the no-data banner's button, whichever change caused it. */
  public revertSelection(): void {
    if (this.lastMismatchCause == 'dataType') {
      this.revertDataType();
    } else {
      this.revertPeriod();
    }
  }

  /** Wording for the no-data banner, matched to whichever change actually caused it. */
  public get noDataBannerTitle(): string {
    return this.lastMismatchCause == 'dataType'
      ? `There is no ${this.dataTypeLabel(this.dataType).toLowerCase()} data available for the selected area.`
      : 'There is no data available for the selected area and time period combination.';
  }

  public get noDataBannerHint(): string {
    return this.lastMismatchCause == 'dataType'
      ? 'Choose a different area above, or switch back to the previous data type.'
      : 'Choose a different area above, or keep your previous time period.';
  }

  public get noDataBannerActionLabel(): string {
    return this.lastMismatchCause == 'dataType' ? 'Keep previous data type' : 'Keep previous time period';
  }

  // Data type ----------------------------------------------------------------

  /**
   * Standard and Modelled are separate SystemLevelTypeIDs on the API, so switching
   * between them is treated the same way as a time period change: the system level
   * and area dropdown is re-fetched under the new type, and an area already
   * selected is re-applied once confirmed to still exist under it.
   */
  public setDataType(type: string): void {
    if (this.dataType == type) {
      return;
    }
    this.dataType = type;
    this.currentDataType.emit(this.dataType);
    this.currentSystemLevelTypeId.emit(this.systemLevelTypeId);
    this.persistDataType();

    if (!this.selectedPeriod) {
      return;
    }
    this.isSelectedAreaInTheSelectedTimePeriod = true;
    this.lastMismatchCause = 'dataType';
    this.pendingApply = !!this.selectedArea;
    if (this.selectedArea) {
      this.checkingIfAreaExists = true;
    }
    this.loadSystemLevels(this.selectedPeriod.TimePeriodID);
  }

  /** Arrow keys move between the two options, as expected of a radio group. */
  public onDataTypeKeydown(event: KeyboardEvent): void {
    if (['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].indexOf(event.key) < 0) {
      return;
    }
    event.preventDefault();
    this.setDataType(this.dataType == 'reported' ? 'modelled' : 'reported');
    setTimeout(() => {
      const options = this.dataTypeOptionEls?.toArray() || [];
      const target = options[this.dataType == 'reported' ? 0 : 1];
      target?.nativeElement.focus();
    });
  }

  public showPopulationRate(periodId: number): boolean {
    // Show population rate for certain time periods
    // Rates were incorrectly calculated for removed periods
    if ([4, 5, 6, 7].includes(periodId)) {
      return false;
    } else {
      return true;
    }
  }
}
